import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Save, Send, Settings } from 'lucide-react';

export const SettingsTab = ({
  settings,
  handleSettingChange,
  saveSettings,
  isSavingSettings,
  showSmtpPassword,
  setShowSmtpPassword,
  testEmailAddress,
  setTestEmailAddress,
  sendTestEmail,
  sendTestInternalNotification,
  isSendingTestEmail,
}) => {
  return (
    <div className="space-y-6">
      {/* SEO Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            SEO Instellingen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="meta_title">Meta Titel</Label>
            <Input
              id="meta_title"
              value={settings.meta_title}
              onChange={(e) => handleSettingChange('meta_title', e.target.value)}
              placeholder="Telecomprijsvergelijker.be - Vergelijk en Bespaar"
              data-testid="meta-title-input"
            />
            <p className="text-xs text-slate-500 mt-1">Wordt getoond in zoekresultaten en browser tabs</p>
          </div>

          <div>
            <Label htmlFor="meta_description">Meta Beschrijving</Label>
            <Textarea
              id="meta_description"
              value={settings.meta_description}
              onChange={(e) => handleSettingChange('meta_description', e.target.value)}
              placeholder="Vergelijk Telenet, Proximus en Base met Orange. Bespaar gemiddeld €750 per jaar op TV, internet en mobiel."
              rows={3}
              data-testid="meta-description-input"
            />
            <p className="text-xs text-slate-500 mt-1">Korte beschrijving voor zoekresultaten (max 160 tekens)</p>
          </div>

          <div>
            <Label htmlFor="meta_keywords">Meta Keywords</Label>
            <Input
              id="meta_keywords"
              value={settings.meta_keywords}
              onChange={(e) => handleSettingChange('meta_keywords', e.target.value)}
              placeholder="telecom vergelijking, Orange, Telenet, Proximus, Base, internet, TV, mobiel"
              data-testid="meta-keywords-input"
            />
            <p className="text-xs text-slate-500 mt-1">Komma-gescheiden keywords</p>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Tracking & Analytics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="google_analytics_id">Google Analytics ID</Label>
            <Input
              id="google_analytics_id"
              value={settings.google_analytics_id}
              onChange={(e) => handleSettingChange('google_analytics_id', e.target.value)}
              placeholder="G-XXXXXXXXXX of UA-XXXXXXXX-X"
              data-testid="ga-id-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Google Analytics Measurement ID voor website tracking
            </p>
          </div>

          <div className="pt-4 border-t">
            <Label htmlFor="conversion_event_name">Conversie Event Naam (Bedankt pagina)</Label>
            <Input
              id="conversion_event_name"
              value={settings.conversion_event_name}
              onChange={(e) => handleSettingChange('conversion_event_name', e.target.value)}
              placeholder="generate_lead"
              data-testid="conversion-event-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Event naam die naar Google Analytics wordt gestuurd wanneer een lead het formulier voltooit (standaard: "generate_lead")
            </p>
          </div>

          <div>
            <Label htmlFor="conversion_value">Conversie Waarde (€)</Label>
            <Input
              id="conversion_value"
              type="number"
              value={settings.conversion_value}
              onChange={(e) => handleSettingChange('conversion_value', e.target.value)}
              placeholder="0"
              data-testid="conversion-value-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Optionele waarde per conversie voor ROI berekening in Google Analytics
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Email Configuratie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="sender_email">Afzender Email</Label>
            <Input
              id="sender_email"
              type="email"
              value={settings.sender_email}
              onChange={(e) => handleSettingChange('sender_email', e.target.value)}
              placeholder="noreply@telecomprijsvergelijker.be"
              data-testid="sender-email-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Email adres dat wordt gebruikt als afzender voor bevestigingsmails
            </p>
          </div>

          <div>
            <Label htmlFor="notification_email">Notificatie Email (Interne Leads)</Label>
            <Input
              id="notification_email"
              type="email"
              value={settings.notification_email}
              onChange={(e) => handleSettingChange('notification_email', e.target.value)}
              placeholder="lead@telecomprijsvergelijker.be"
              data-testid="notification-email-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Email adres waar nieuwe lead notificaties naartoe worden gestuurd
            </p>
          </div>

          {/* Test Notificatie */}
          <div className="pt-4 border-t">
            <Label className="mb-2 block font-medium">Test Interne Notificatie</Label>
            <p className="text-xs text-slate-500 mb-3">
              Verstuur een test notificatie naar het notificatie email adres om te controleren of nieuwe leads correct worden gemeld.
            </p>
            <Button
              onClick={sendTestInternalNotification}
              disabled={isSendingTestEmail || !settings.notification_email}
              variant="outline"
              data-testid="send-test-notification-button"
            >
              {isSendingTestEmail ? (
                <>
                  <div className="spinner mr-2" />
                  Verzenden...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Test Notificatie Verzenden
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Settings */}
      <Card>
        <CardHeader>
          <CardTitle>SMTP Instellingen (Nomeo)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              Vul hier de SMTP gegevens van uw Nomeo hosting in om emails te kunnen versturen.
              Deze vindt u in uw Nomeo controlepaneel onder "E-mail instellingen".
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="smtp_host">SMTP Server</Label>
              <Input
                id="smtp_host"
                value={settings.smtp_host}
                onChange={(e) => handleSettingChange('smtp_host', e.target.value)}
                placeholder="mail.telecomprijsvergelijker.be"
                data-testid="smtp-host-input"
              />
            </div>

            <div>
              <Label htmlFor="smtp_port">SMTP Poort</Label>
              <Input
                id="smtp_port"
                value={settings.smtp_port}
                onChange={(e) => handleSettingChange('smtp_port', e.target.value)}
                placeholder="587"
                data-testid="smtp-port-input"
              />
              <p className="text-xs text-slate-500 mt-1">Meestal 587 (TLS) of 465 (SSL)</p>
            </div>
          </div>

          <div>
            <Label htmlFor="smtp_username">SMTP Gebruikersnaam</Label>
            <Input
              id="smtp_username"
              value={settings.smtp_username}
              onChange={(e) => handleSettingChange('smtp_username', e.target.value)}
              placeholder="noreply@telecomprijsvergelijker.be"
              data-testid="smtp-username-input"
            />
            <p className="text-xs text-slate-500 mt-1">Meestal het volledige email adres</p>
          </div>

          <div>
            <Label htmlFor="smtp_password">SMTP Wachtwoord</Label>
            <div className="relative">
              <Input
                id="smtp_password"
                type={showSmtpPassword ? "text" : "password"}
                value={settings.smtp_password}
                onChange={(e) => handleSettingChange('smtp_password', e.target.value)}
                placeholder="••••••••"
                data-testid="smtp-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowSmtpPassword(!showSmtpPassword)}
              >
                {showSmtpPassword ? 'verberg' : 'toon'}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="smtp_use_tls"
              checked={settings.smtp_use_tls === 'true'}
              onChange={(e) => handleSettingChange('smtp_use_tls', e.target.checked ? 'true' : 'false')}
              className="w-4 h-4"
            />
            <Label htmlFor="smtp_use_tls" className="cursor-pointer">
              Gebruik TLS (aanbevolen voor poort 587)
            </Label>
          </div>

          {/* Test Email Section */}
          <div className="pt-4 border-t mt-4">
            <Label className="mb-2 block font-medium">Test Email Verzenden</Label>
            <p className="text-xs text-slate-500 mb-3">
              Verstuur een test email met een fictieve lead om te controleren of uw SMTP instellingen correct zijn.
            </p>
            <div className="flex gap-3">
              <Input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="uw-email@voorbeeld.be"
                className="flex-1"
                data-testid="test-email-input"
              />
              <Button
                onClick={sendTestEmail}
                disabled={isSendingTestEmail || !testEmailAddress}
                variant="outline"
                data-testid="send-test-email-button"
              >
                {isSendingTestEmail ? (
                  <>
                    <div className="spinner mr-2" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Test Verzenden
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={isSavingSettings}
          data-testid="save-settings-button"
          size="lg"
        >
          {isSavingSettings ? (
            <>
              <div className="spinner mr-2" />
              Opslaan...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Instellingen Opslaan
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
